import { Extensions, generateHTML, generateJSON } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color, BackgroundColor, FontSize } from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import { Table, TableRow, TableHeader, TableCell } from "@tiptap/extension-table";
import Image from "@tiptap/extension-image";
import { TEXT_PALETTE, BG_PALETTE } from "../write/plugin/inlineExecStyleTool";

// FreeText 게시글 공통 설정
// 작성(FreeTextEditor)과 열람(FreeTextViewer)이 같은 확장 구성을 써야 저장한 그대로 보인다.
// 블록 방식 → FreeText 전환 시에도 이 구성을 기준으로 HTML 을 만들면 된다.

// 글자 크기 단계 (기본 = 지정 안 함 = 본문 16px)
export const DEFAULT_FONT_SIZE = "16px";
export const FONT_SIZES = ["12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px"];

// 색 팔레트는 블록 편집기 색 메뉴와 같은 값을 쓴다 (맨 앞 "기본" = 색 제거)
export interface PaletteColor {
  name: string;
  value?: string;
  clear?: boolean;
}
export const TEXT_COLORS: PaletteColor[] = TEXT_PALETTE;
export const BACKGROUND_COLORS: PaletteColor[] = BG_PALETTE;

export const createFreeTextExtensions = (): Extensions => [
  StarterKit.configure({
    heading: { levels: [1, 2, 3, 4] },
    code: false,
    codeBlock: false,
    link: {
      openOnClick: false,
      autolink: true,
      defaultProtocol: "https",
      HTMLAttributes: { target: "_blank", rel: "noopener noreferrer nofollow" },
    },
  }),
  TextStyle,
  Color,
  BackgroundColor,
  FontSize,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
  // 붙여넣은 base64 이미지는 저장하지 않는다 (업로드 버튼으로 넣은 이미지만 허용)
  Image.configure({ allowBase64: false }),
];

const SCHEMA_EXTENSIONS = createFreeTextExtensions();

/**
 * 저장된 HTML 을 FreeText 스키마로 다시 만든다.
 * 스키마에 없는 태그 / 속성은 버려지므로 화면에 그리기 전 정리용으로 쓴다 (서버 정리와 별개로 한 번 더).
 */
export const normalizeFreeTextHtml = (html?: string | null): string => {
  if (!html) return "";
  try {
    return generateHTML(generateJSON(html, SCHEMA_EXTENSIONS), SCHEMA_EXTENSIONS);
  } catch (e) {
    console.error("FreeText HTML 변환 오류", e);
    return "";
  }
};

/** 본문 HTML 에 실제 내용(글자, 이미지, 표, 구분선)이 있는지 */
export const hasFreeTextContent = (html?: string | null): boolean => {
  if (!html) return false;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").trim().length > 0 || !!doc.body.querySelector("img, table, hr");
};

// ── 붙여넣기 정리 ──
// 글꼴은 지우고(스키마에 글꼴 확장이 없어 자동으로 빠짐), 글자 크기는 가장 가까운 단계로 맞춘다.
// pt 단위(워드, 한글 문서)는 px 로 바꾼다. 내장(data:) 이미지는 지운다.
const toPx = (value: string): number | null => {
  const matched = value.trim().match(/^(\d+(?:\.\d+)?)(px|pt)$/i);
  if (!matched) return null;
  const size = parseFloat(matched[1]);
  return matched[2].toLowerCase() === "pt" ? (size * 4) / 3 : size;
};

const nearestFontSize = (px: number): string =>
  FONT_SIZES.reduce((nearest, size) =>
    Math.abs(parseFloat(size) - px) < Math.abs(parseFloat(nearest) - px) ? size : nearest
  );

/** 글자 크기를 FONT_SIZES 단계로 맞춘다 (기본 크기, 알 수 없는 단위는 지움) */
export const snapFontSizes = (root: ParentNode) => {
  root.querySelectorAll<HTMLElement>("[style]").forEach((el) => {
    const fontSize = el.style.fontSize;
    if (fontSize) {
      const px = toPx(fontSize);
      if (px === null) {
        el.style.removeProperty("font-size");
      } else {
        const snapped = nearestFontSize(px);
        if (snapped === DEFAULT_FONT_SIZE) el.style.removeProperty("font-size");
        else el.style.fontSize = snapped;
      }
    }
  });
};

export const normalizePastedHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  snapFontSizes(doc.body);

  // 우리 서버에 올린 이미지만 남긴다 (다른 사이트 이미지는 나중에 깨질 수 있고, 워드 / 한글 문서 이미지는 로컬 경로라 보이지 않음)
  const backendUrl = process.env.REACT_APP_BACKEND_URL ?? "";
  doc.body.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src") ?? "";
    const isOwnImage = (backendUrl !== "" && src.startsWith(backendUrl)) || src.startsWith("/api/photo/");
    if (!isOwnImage) img.remove();
  });

  return doc.body.innerHTML;
};
